import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { Button, Callout, Card, Flex, ServerErrorCallout, Text } from '@/components/common';
import { AccountSubpageShell } from '@/components/layout';
import { useAuth } from '@/hooks/useAuth';
import { analyticsClient } from '@/lib';
import { gdprService } from '@/services';

export function RequestDataPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPending, setHasPending] = useState(false);
  const [checkingPending, setCheckingPending] = useState(true);

  const handleSend = async () => {
    setLoading(true);
    setError(null);
    try {
      await gdprService.requestDataExport();
      analyticsClient.capture('data_export_requested');
      toast.success(t('requestData.success'));
      setHasPending(true);
    } catch (err) {
      console.error('Failed to request data export:', err);
      setError(err instanceof Error ? err.message : t('requestData.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const check = async () => {
      const pending = await gdprService.hasPendingDataExport(user.uid);
      if (!cancelled) {
        setHasPending(pending);
        setCheckingPending(false);
      }
    };
    void check();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <AccountSubpageShell
      onBack={() => navigate('/account/settings/about')}
      title={t('requestData.title')}
      subtitle={t('requestData.subtitle')}
    >
      <Flex direction="column" gap="4">
        <Card>
          <Text
            as="div"
            size="1"
            weight="bold"
            color="gray"
            mb="1"
            style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
          >
            {t('requestData.whatYouGetHeading')}
          </Text>
          <Text as="div" size="1" color="gray">
            {t('requestData.whatYouGetBody')}
          </Text>
        </Card>

        <Card>
          <Text
            as="div"
            size="1"
            weight="bold"
            color="gray"
            mb="1"
            style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
          >
            {t('requestData.howItWorksHeading')}
          </Text>
          <Text as="div" size="1" color="gray">
            {t('requestData.howItWorksBody')}
          </Text>
        </Card>

        {hasPending && <Callout>{t('requestData.pending')}</Callout>}

        {error && <ServerErrorCallout message={error} />}

        <Flex direction="column" gap="2" mt="2">
          <Button
            type="button"
            color="gray"
            highContrast
            fullWidth
            disabled={loading || hasPending || checkingPending}
            isLoading={loading}
            onClick={() => void handleSend()}
          >
            {t('requestData.send')}
          </Button>
          <Button
            type="button"
            variant="outline"
            color="gray"
            fullWidth
            onClick={() => navigate('/account/settings/about')}
          >
            {t('requestData.cancel')}
          </Button>
        </Flex>
      </Flex>
    </AccountSubpageShell>
  );
}
